// src/hooks/useCalendarData.jsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import moment from "moment";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  deleteDoc,
  doc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { getSubCollection } from "../utils/getSubCollection"; // <-- ajusta si tu ruta difiere

/** Utilidad común: trae docs donde eres owner O estás invited, con dedupe O(1) */
const useFetchOwnedAndInvited = (db) =>
  useCallback(
    async (colPath, ownerUid) => {
      const colRef = collection(db, colPath);
      const [ownSnap, invitedSnap] = await Promise.all([
        getDocs(query(colRef, where("ownerId", "==", ownerUid))),
        getDocs(query(colRef, where("invited", "array-contains", ownerUid))),
      ]);

      const map = new Map();
      ownSnap.docs.forEach((d) => map.set(d.id, d));
      invitedSnap.docs.forEach((d) => {
        if (!map.has(d.id)) map.set(d.id, d);
      });

      return [...map.values()].map((d) => ({ id: d.id, ...d.data() }));
    },
    [db]
  );

export default function useCalendarData(ownerUid, currentYear) {
  const db = useMemo(() => getFirestore(), []);
  const fetchOwnedAndInvited = useFetchOwnedAndInvited(db);
  const catsReqId = useRef(0);
  const dataReqId = useRef(0);
  const usersReqId = useRef(0);

  // --- estado expuesto por el hook ---
  const [loading, setLoading] = useState(true);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [filters, setFilters] = useState([]); // categorías dinámicas
  const [fsEvents, setFsEvents] = useState([]); // cotizaciones + eventos + tareas
  const [proximasAVencer, setProximasAVencer] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(["Todos"]);

  const getFechasFestivas = useCallback((year) => {
    const fechasFestivasBase = [
      { title: "Año Nuevo", month: "01", day: "01", color: "#fee2e2" },
      {
        title: "Día de la Constitución",
        month: "02",
        day: "05",
        color: "#fee2e2",
      },
      {
        title: "Natalicio de Benito Juárez",
        month: "03",
        day: "21",
        color: "#fee2e2",
      },
      { title: "Día del Trabajo", month: "05", day: "01", color: "#fee2e2" },
      {
        title: "Independencia de México",
        month: "09",
        day: "16",
        color: "#fee2e2",
      },
      {
        title: "Revolución Mexicana",
        month: "11",
        day: "20",
        color: "#fee2e2",
      },
      { title: "Navidad", month: "12", day: "25", color: "#fee2e2" },
    ];
    return fechasFestivasBase.map((festivo) => ({
      id: `festivo-${festivo.title}`,
      title: festivo.title,
      start: moment(
        `${year}-${festivo.month}-${festivo.day}`,
        "YYYY-MM-DD"
      ).toDate(),
      end: moment(
        `${year}-${festivo.month}-${festivo.day}`,
        "YYYY-MM-DD"
      ).toDate(),
      allDay: true,
      resource: "festivo",
      color: festivo.color,
    }));
  }, []);

  const combinedEvents = useMemo(() => {
    const y = currentYear || moment().year();
    const festivos = getFechasFestivas(y);
    return [...festivos, ...fsEvents];
  }, [currentYear, fsEvents, getFechasFestivas]);

  const fixedFilterOptions = useMemo(() => {
    const base = ["Todos", "Festivos"];
    const baseLower = new Set(base.map((b) => b.toLowerCase()));
    const dynamic = [];

    for (const f of filters) {
      const lbl = f.label.trim();
      const low = lbl.toLowerCase();
      if (
        !baseLower.has(low) &&
        !dynamic.some((l) => l.toLowerCase() === low)
      ) {
        dynamic.push(lbl);
      }
    }
    return [...base, ...dynamic];
  }, [filters]);

  const toggleFixedFilter = useCallback((filter) => {
    setSelectedFilters((prev) => {
      if (prev.includes(filter)) {
        return prev.filter((f) => f !== filter);
      } else {
        if (filter === "Todos") return ["Todos"];
        const newFilters = prev.filter((f) => f !== "Todos");
        return [...newFilters, filter];
      }
    });
  }, []);

  const filteredEvents = useMemo(() => {
    if (selectedFilters.length === 0 || selectedFilters.includes("Todos")) {
      return combinedEvents;
    }
    const filterLowers = selectedFilters.map((f) => f.trim().toLowerCase());

    return combinedEvents.filter((entry) => {
      const res = entry.resource; // evento | tarea | cotizacion | festivo
      const fSlug = (entry.categorySlug ?? entry.category ?? "")
        .trim()
        .toLowerCase();

      if (filterLowers.includes("eventos") && res === "evento") return true;
      if (filterLowers.includes("festivos") && res === "festivo") return true;
      if (filterLowers.includes("cotizaciones") && res === "cotizacion")
        return true;
      if (res === "tarea" && filterLowers.includes(fSlug)) return true;

      return false;
    });
  }, [selectedFilters, combinedEvents]);

  // ---------- shared users ----------
  const fetchSharedUsers = useCallback(async () => {
    if (!ownerUid) return;
    const reqId = ++usersReqId.current;

    try {
      const [ownSnap, invitedSnap] = await Promise.all([
        getDocs(
          query(collection(db, "usuarios"), where("ownerId", "==", ownerUid))
        ),
        getDocs(
          query(
            collection(db, "usuarios"),
            where("invited", "array-contains", ownerUid)
          )
        ),
      ]);

      const map = new Map();
      ownSnap.docs.forEach((d) => map.set(d.id, d));
      invitedSnap.docs.forEach((d) => {
        if (!map.has(d.id)) map.set(d.id, d);
      });

      const usuarios = [...map.values()].map((d) => {
        const data = d.data();
        return {
          id: d.id,
          nombre: data.nombre,
          apellido: data.apellido,
          color: data.colorIcono || "#A3A3A3",
        };
      });

      if (reqId === usersReqId.current) setSharedUsers(usuarios);
    } catch (error) {
      console.error("Error al cargar usuarios compartidos:", error);
    }
  }, [db, ownerUid]);

  // ---------- categorías (con siembra inicial) ----------
  const fetchCategorias = useCallback(async () => {
    if (!ownerUid) return;
    const reqId = ++catsReqId.current;

    try {
      let docs = await fetchOwnedAndInvited("filtrosCategorias", ownerUid);

      const seedFlag = `seededCats_${ownerUid}`;
      if (docs.length === 0 && !localStorage.getItem(seedFlag)) {
        const defaults = [
          { label: "Eventos", slug: "eventos", color: "#6366F1" },
          { label: "Cotizaciones", slug: "cotizaciones", color: "#3B82F6" },
        ];
        const batch = writeBatch(db);
        const colRef = collection(db, "filtrosCategorias");

        defaults.forEach((def) => {
          const newRef = doc(colRef);
          batch.set(newRef, {
            ...def,
            ownerId: ownerUid,
            invited: [],
            createdAt: moment().toISOString(),
          });
          docs.push({ id: newRef.id, ...def }); // reflejo local inmediato
        });

        await batch.commit();
        localStorage.setItem(seedFlag, "1");
      }

      if (reqId !== catsReqId.current) return;

      const categoriasData = docs.map((d) => ({
        id: d.id,
        label: d.label,
        slug: d.slug,
        color: d.color || "#f0f0f0",
      }));
      setFilters(categoriasData);
    } catch (err) {
      console.error("Error al cargar categorías:", err);
      Swal.fire("Error", "No se pudieron cargar las categorías.", "error");
    }
  }, [db, ownerUid, fetchOwnedAndInvited]);

  // ---------- datos Firestore: cotizaciones + eventos + tareas ----------
  const fetchFirestoreData = useCallback(async () => {
    if (!ownerUid) return;
    const reqId = ++dataReqId.current;

    try {
      const now = moment();
      const todayStart = now.clone().startOf("day").valueOf();

      const cotSnap = await getDocs(getSubCollection(ownerUid, "cotizaciones"));
      const cotizaciones = cotSnap.docs
        .map((d) => {
          const data = d.data();
          const fv = data.fechaVencimiento;
          if (!fv) return null;

          const startMs = moment(fv).startOf("day").valueOf();
          const endMs = moment(fv).endOf("day").valueOf();

          return {
            id: `cotizacion-${d.id}`,
            title: `Cotización #${data.numeroCotizacion || "Sin número"}`,
            numeroCotizacion: data.numeroCotizacion || "Sin número",
            start: new Date(startMs),
            end: new Date(endMs),
            allDay: true,
            resource: "cotizacion",
            color: "#dbeafe",
            note: data.note || "",
            vence: moment(fv).format("DD/MM/YYYY"),
            _startMs: startMs,
          };
        })
        .filter(Boolean);

      const proximas = cotizaciones
        .filter((c) => c._startMs >= todayStart)
        .sort((a, b) => a._startMs - b._startMs)
        .slice(0, 6)
        .map(({ _startMs, ...rest }) => rest);

      const [eventosDocs, tareasDocs] = await Promise.all([
        fetchOwnedAndInvited("eventos", ownerUid),
        fetchOwnedAndInvited("tareaCalendario", ownerUid),
      ]);

      const eventos = eventosDocs.map((d) => ({
        id: `evento-${d.id}`,
        title: d.title,
        start: moment(d.from).toDate(),
        end: moment(d.to).toDate(),
        allDay: true,
        resource: "evento",
        color: d.color,
        note: d.note || "",
      }));

      const tareas = tareasDocs.map((d) => ({
        id: `tarea-${d.id}`,
        title: d.title,
        start: moment(d.from).toDate(),
        end: moment(d.to).toDate(),
        allDay: true,
        resource: "tarea",
        color: d.color || "#f0f0f0",
        category: d.category || null,
        categorySlug:
          (d.categorySlug ?? d.category ?? "")
            .toString()
            .trim()
            .toLowerCase() || null,
        note: d.note || "",
      }));

      if (reqId !== dataReqId.current) return;

      setProximasAVencer(proximas);
      setProximosEventos(eventos);
      setFsEvents([...cotizaciones, ...eventos, ...tareas]);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los datos.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      if (reqId === dataReqId.current) setLoading(false);
    }
  }, [ownerUid, fetchOwnedAndInvited]);

  // ---------- acciones públicas ----------
  const refreshData = useCallback(async () => {
    if (!ownerUid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    await fetchFirestoreData();
  }, [ownerUid, fetchFirestoreData]);

  const handleAddCategory = useCallback(
    async (newCategoryName, chosenColor) => {
      try {
        const label = newCategoryName.trim();
        if (!label) return;

        await addDoc(collection(db, "filtrosCategorias"), {
          label,
          slug: label.toLowerCase(),
          color: chosenColor || "#f0f0f0",
          ownerId: ownerUid,
          invited: [],
          createdAt: moment().toISOString(),
        });

        await fetchCategorias();

        Swal.fire({
          title: "Categoría creada",
          text: `La categoría "${label}" ha sido agregada.`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al crear nueva categoría:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo crear la nueva categoría.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    },
    [db, ownerUid, fetchCategorias]
  );

  const handleSaveEvent = useCallback(
    async ({ id, title, start, end, category, color, note }) => {
      try {
        const from = moment(start).toISOString();
        const to = moment(end).toISOString();

        const newResource =
          category && category !== "Eventos" ? "tarea" : "evento";
        const newCategory =
          category && category !== "Eventos" ? category : null;
        const categorySlug = newCategory
          ? newCategory.trim().toLowerCase()
          : null;

        if (id) {
          // edición
          const wasEvento = id.startsWith("evento-");
          const oldResource = wasEvento ? "evento" : "tarea";
          const docId = id.replace(/^(evento-|tarea-)/, "");

          if (oldResource === newResource) {
            const collectionName =
              oldResource === "evento" ? "eventos" : "tareaCalendario";
            await updateDoc(doc(db, collectionName, docId), {
              title,
              from,
              to,
              color: color || "#f0f0f0",
              note,
              resource: newResource,
              category: newCategory,
              categorySlug,
              ownerId: ownerUid,
              invited: [],
            });
          } else {
            // cambio de tipo: crear en nueva colección + borrar antiguo
            const newCollection =
              newResource === "evento" ? "eventos" : "tareaCalendario";
            await addDoc(collection(db, newCollection), {
              title,
              from,
              to,
              color: color || "#f0f0f0",
              note,
              resource: newResource,
              category: newCategory,
              categorySlug,
              ownerId: ownerUid,
              invited: [],
            });

            const oldCollection =
              oldResource === "evento" ? "eventos" : "tareaCalendario";
            await deleteDoc(doc(db, oldCollection, docId));
          }
        } else {
          // creación
          const collectionName =
            newResource === "evento" ? "eventos" : "tareaCalendario";
          await addDoc(collection(db, collectionName), {
            title,
            from,
            to,
            color: color || "#f0f0f0",
            note,
            resource: newResource,
            category: newCategory,
            categorySlug,
            ownerId: ownerUid,
            invited: [],
          });
        }

        Swal.fire({
          title: id ? "Evento actualizado" : "Evento creado",
          text: id
            ? "El evento se ha actualizado correctamente."
            : "El evento ha sido agregado.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        await refreshData();
      } catch (error) {
        console.error("Error al guardar:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo guardar el evento/tarea.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    },
    [db, ownerUid, refreshData]
  );

  const handleDeleteEvent = useCallback(
    async (ev) => {
      const { isConfirmed } = await Swal.fire({
        title: "¿Seguro que quieres eliminar este evento?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;

      try {
        let collectionName = "";
        let docId = "";

        if (ev.id.startsWith("evento-")) {
          collectionName = "eventos";
          docId = ev.id.replace("evento-", "");
        } else if (ev.id.startsWith("tarea-")) {
          collectionName = "tareaCalendario";
          docId = ev.id.replace("tarea-", "");
        } else if (ev.id.startsWith("cotizacion-")) {
          collectionName = "cotizaciones";
          docId = ev.id.replace("cotizacion-", "");
        } else {
          return; // festivos u otros tipos no soportados
        }

        await deleteDoc(doc(db, collectionName, docId));
        Swal.fire("Eliminado", "El evento se ha eliminado.", "success");
        await refreshData();
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "No se pudo eliminar el evento.", "error");
      }
    },
    [db, refreshData]
  );

  // ---------- carga inicial ----------
  useEffect(() => {
    if (!ownerUid) return; // esperar a tener UID

    let cancelled = false;

    (async () => {
      setLoading(true);
      await Promise.all([
        fetchCategorias(),
        fetchSharedUsers(),
        fetchFirestoreData(),
      ]);
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
      // invalidar cualquier request en vuelo por loader
      catsReqId.current += 1;
      dataReqId.current += 1;
      usersReqId.current += 1;
    };
  }, [ownerUid, fetchCategorias, fetchSharedUsers, fetchFirestoreData]);

  // --- API pública del hook ---
  return {
    // state
    loading,
    sharedUsers,
    filters,
    fsEvents,
    proximasAVencer,
    proximosEventos,
    combinedEvents,
    fixedFilterOptions,
    filteredEvents,
    selectedFilters,
    // actions
    refreshData,
    fetchCategorias,
    handleAddCategory,
    handleSaveEvent,
    handleDeleteEvent,
    toggleFixedFilter,
    setSelectedFilters,
  };
}
